import React, { useState, useEffect } from "react";
import { businessTypeAdminAPI } from "../../api/apiService";
import { Plus, Edit2, Trash2, X, RefreshCw, Briefcase, Info } from "lucide-react";
import toast from "react-hot-toast";

const BusinessTypeManagement = () => {
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({ name: "", description: "", is_active: true });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        fetchTypes();
    }, []);

    const fetchTypes = async () => {
        try {
            setLoading(true);
            const res = await businessTypeAdminAPI.getAll();
            setTypes(res.data);
        } catch (error) {
            toast.error("Failed to fetch business types");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleCreateNew = () => {
        setFormData({ name: "", description: "", is_active: true });
        setEditingId(null);
        setFormOpen(true);
    };

    const handleEdit = (type) => {
        setFormData({
            name: type.name,
            description: type.description || "",
            is_active: type.is_active,
        });
        setEditingId(type.id);
        setFormOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this business type? This may affect users who have selected this type.")) return;
        try {
            await businessTypeAdminAPI.delete(id);
            toast.success("Business type deleted successfully");
            fetchTypes();
        } catch (error) {
            toast.error("Failed to delete business type");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            toast.error("Please enter a name");
            return;
        }

        try {
            setIsSubmitting(true);
            if (editingId) {
                await businessTypeAdminAPI.update(editingId, formData);
                toast.success("Business type updated successfully");
            } else {
                await businessTypeAdminAPI.create(formData);
                toast.success("Business type created successfully");
            }
            setFormOpen(false);
            fetchTypes();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to save business type");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Business Type Management</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                        Manage categories of businesses allowed in the system.
                    </p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={fetchTypes}
                        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-200"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                    <button
                        onClick={handleCreateNew}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:from-emerald-700 hover:to-teal-700 shadow-md shadow-emerald-500/20 transition-all font-medium"
                    >
                        <Plus className="w-4 h-4" />
                        Add Business Type
                    </button>
                </div>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20 rounded-xl p-4 flex gap-3 text-amber-800 dark:text-amber-400">
                <Info className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">
                    <strong>Note:</strong> Business types are displayed in the registration dropdown. Disabling or deleting a type will prevent new users from selecting it.
                </p>
            </div>

            {loading && !formOpen ? (
                <div className="flex justify-center items-center h-64">
                    <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
                </div>
            ) : types.length === 0 ? (
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-8 text-center flex flex-col items-center justify-center min-h-[300px]">
                    <Briefcase className="w-12 h-12 text-slate-300 mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No business types found</h3>
                    <button
                        onClick={handleCreateNew}
                        className="mt-4 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
                    >
                        Create First Type
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {types.map((type) => (
                        <div key={type.id} className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-all flex flex-col justify-between">
                            <div>
                                <div className="flex justify-between items-start mb-3">
                                    <h3 className="text-lg font-bold text-slate-900 dark:text-white">{type.name}</h3>
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${type.is_active ? 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400' : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300'}`}>
                                        {type.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 line-clamp-2">
                                    {type.description || "No description provided."}
                                </p>
                            </div>
                            <div className="flex gap-2 pt-4 border-t border-slate-50 dark:border-slate-700/50">
                                <button
                                    onClick={() => handleEdit(type)}
                                    className="flex-1 p-2 rounded-lg bg-slate-50 hover:bg-slate-100 dark:bg-slate-700/50 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-colors flex justify-center items-center gap-2 text-xs font-bold"
                                >
                                    <Edit2 className="w-3.5 h-3.5" />
                                    Edit
                                </button>
                                <button
                                    onClick={() => handleDelete(type.id)}
                                    className="flex-1 p-2 rounded-lg bg-red-50 hover:bg-red-100 dark:bg-red-500/10 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400 transition-colors flex justify-center items-center gap-2 text-xs font-bold"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal Form */}
            {formOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-md border border-slate-200 dark:border-slate-700 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                                {editingId ? "Edit Business Type" : "Add Business Type"}
                            </h3>
                            <button
                                onClick={() => setFormOpen(false)}
                                className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1 ml-1">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                                    placeholder="e.g. Pharmacy"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1 ml-1">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className="w-full px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                                    placeholder="Brief description of this business type..."
                                ></textarea>
                            </div>

                            <div className="flex items-center gap-2 pt-2">
                                <input
                                    type="checkbox"
                                    id="is_active_bt"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleInputChange}
                                    className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                />
                                <label htmlFor="is_active_bt" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Active (Visible in selection)
                                </label>
                            </div>

                            <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setFormOpen(false)}
                                    className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-lg transition-colors text-sm font-bold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all font-bold disabled:opacity-70 flex items-center gap-2"
                                >
                                    {isSubmitting ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                                    {editingId ? "Save Changes" : "Create Type"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BusinessTypeManagement;
